<template>
  <div class="vault-details p-4 md:p-6">
    <form
      class="h-full relative"
      @submit.prevent="validateBeforeSubmit()"
    >
      <div class="pb-2">
        <!-- One action slot by the heading: Edit Details in view mode, Cancel and Update in edit mode -->
        <div class="mb-1 pb-1 border-b flex items-end justify-between">
          <p class="text-lg">
            <i class="fa fa-info-circle text-brand"></i> Vault Details
          </p>
          <!-- The wrapper stays mounted (v-if only inside it) so v-is-authorized binds once, at first render -->
          <span
            key="edit-action"
            class="flex mb-1"
            v-is-authorized="{
              roles: [role],
              permissions: ['can_edit_vault_details'],
            }"
          >
            <template v-if="edit">
              <button
                type="button"
                class="mr-2 border border-grey bg-white text-grey-darker text-sm font-medium py-2 px-4 rounded hover:bg-grey-lightest"
                @click.prevent="edit = false"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="border border-brand bg-brand text-white text-sm font-medium py-2 px-4 rounded hover:bg-brand-darkest"
                :class="{ 'cursor-not-allowed opacity-75': updating }"
                :disabled="updating"
              >
                <i class="fa mr-1" :class="updating ? 'fa-spin fa-spinner' : 'fa-save'"></i>
                {{ updating ? "Updating" : "Update" }}
              </button>
            </template>
            <button
              v-else
              type="button"
              class="border border-brand bg-white text-brand text-sm font-medium py-2 px-4 rounded hover:bg-brand hover:text-white"
              @click.prevent="editDetails"
            >
              <i class="fa fa-edit mr-1"></i> Edit Details
            </button>
          </span>
        </div>
        <div class="flex flex-wrap">
          <div class="w-full py-1 md:w-1/2 md:pr-8">
            <label class="font-medium">Vault Name</label>

            <input
              type="text"
              v-model="edit_vault.name"
              class="w-full border py-2 px-3 mt-1 rounded"
              v-if="edit"
            />

            <p v-else class="field-value">
              {{ vault.name ? vault.name : "N/A" }}
            </p>
          </div>

          <div class="w-full py-1 md:w-1/2">
            <label class="font-medium">Vault Email Address</label>
            <p class="field-value">
              {{ vault.email_address ? vault.email_address : "N/A" }}
            </p>
            <span class="text-xs text-grey-darker block mt-1">
              Email documents to this address to add them to the vault. Attachments up to 10MB.
            </span>
          </div>
          <div
            class="w-full py-1 md:w-1/3 md:pr-8"
            v-if="vault_type == 'strata'"
          >
            <label class="font-medium">Lots</label>

            <input
              type="text"
              v-model="edit_vault.number_of_lots"
              class="w-full border py-2 px-3 mt-1 rounded"
              v-if="edit"
            />

            <p v-else class="field-value">
              {{ vault.number_of_lots ? vault.number_of_lots : "N/A" }}
            </p>
          </div>
          <div class="w-full py-1 md:w-1/3 md:pr-8">
            <label class="font-medium" v-if="vault_type == 'strata'"
              >Building Type</label
            >
            <label class="font-medium" v-if="vault_type == 'business'"
              >Primary Business Activity</label
            >

            <input
              type="text"
              v-model="edit_vault.building_type"
              class="w-full border py-2 px-3 mt-1 rounded"
              v-if="edit"
            />

            <p v-else class="field-value">
              {{ vault.building_type ? vault.building_type : "N/A" }}
            </p>
          </div>
          <div class="w-full py-1 md:w-1/3" v-if="vault_type == 'strata'">
            <label class="font-medium">{{ isNZ || isNZStates(vault.state) ? '' : vault_name }} Plan Number</label>

            <input
              type="text"
              v-model="edit_vault.strata_plan_number"
              class="w-full border py-2 px-3 mt-1 rounded"
              v-if="edit"
            />

            <p v-else class="field-value">
              {{ vault.strata_plan_number ? vault.strata_plan_number : "N/A" }}
            </p>
          </div>
        </div>
      </div>

      <div class="pb-2">
        <p class="mt-6 mb-1 pb-1 border-b text-lg">
          <i class="fa fa-address-card text-brand"></i> Address Details
        </p>
        <div class="flex flex-wrap">
          <div class="w-full py-1 md:w-1/2 md:pr-8">
            <label class="font-medium">Address</label>

            <div v-if="edit">
              <input
                type="text"
                class="w-full border py-2 px-3 mt-1 rounded"
                name="address"
                v-model="edit_vault.address"
              />
            </div>

            <p v-else class="field-value">
              {{ vault.address ? vault.address : "N/A" }}
            </p>
          </div>

          <div class="w-full py-1 md:w-1/2">
            <label class="font-medium">Suburb</label>

            <input
              type="text"
              v-model="edit_vault.suburb"
              class="w-full border py-2 px-3 mt-1 rounded"
              v-if="edit"
            />

            <p v-else class="field-value">
              {{ vault.suburb ? vault.suburb : "N/A" }}
            </p>
          </div>
        </div>
        <div class="flex flex-wrap">
          <div class="w-full py-1 md:w-1/3 md:pr-8">
            <label class="font-medium">Country</label>

            <multiselect
              class="mt-1"
              v-model="country"
              name="country"
              placeholder="Please Select"
              track-by="code"
              label="name"
              :options="countries"
              :showLabels="false"
              :searchable="false"
              :allow-empty="false"
              v-if="edit"
            ></multiselect>

            <p class="field-value" v-else>
              {{ isNZStates(vault.state) ? "New Zealand" : "Australia" }}
            </p>
          </div>
          <div class="w-full py-1 md:w-1/3 md:pr-8">
            <label class="font-medium">{{ isNZ || isNZStates(vault.state) ? 'Type' : 'State' }}</label>

            <multiselect
              class="mt-1"
              v-model="edit_vault.state"
              name="state"
              placeholder="Please Select"
              track-by="code"
              label="name"
              :options="states"
              :showLabels="false"
              :searchable="false"
              v-if="edit"
            ></multiselect>

            <p class="field-value" v-else>
              {{ vault.state }}
            </p>
          </div>
          <div class="w-full py-1 md:w-1/3">
            <label class="font-medium">Postcode</label>

            <input
              type="text"
              v-model="edit_vault.postcode"
              class="w-full border py-2 px-3 mt-1 rounded"
              v-if="edit"
            />

            <p v-else class="field-value">
              {{ vault.postcode ? vault.postcode : "N/A" }}
            </p>
          </div>
        </div>
      </div>

      <div class="pb-2" key="storage" v-if="vault.max_storage_in_megabytes">
        <p class="mt-6 mb-1 pb-1 border-b text-lg">
          <i class="fa fa-database text-brand"></i> Storage Details
        </p>
        <div class="flex flex-wrap">
          <div class="w-full py-1 md:w-1/2 md:pr-8">
            <label class="font-medium">Storage used</label>
            <p class="py-2">
              {{ convertToGB(vault.total_size_in_megabytes) }} GB of
              {{ convertToGB(vault.max_storage_in_megabytes) }} GB used
            </p>
            <div class="w-full h-2 rounded-full bg-grey-lighter overflow-hidden">
              <div
                class="h-full rounded-full"
                :class="storageUsedPercent >= 90 ? 'bg-red' : 'bg-brand'"
                :style="{ width: storageUsedPercent + '%' }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Features: the per-vault switches, data-driven (see `features`) -->
      <div class="pb-2" key="features" v-if="vault_type == 'strata'">
        <p class="mt-6 mb-1 pb-1 border-b text-lg">
          <i class="fa fa-toggle-on text-brand"></i> Features
        </p>
        <div class="flex flex-wrap">
        <div
          v-for="feature in features"
          :key="feature.key"
          class="w-full md:w-1/2 md:pr-6 flex items-start py-2 border-b border-grey-lighter"
        >
          <!-- One switch for both modes; the library dims it when disabled -->
          <toggle-button
            class="flex-none mr-3 mt-1"
            :value="isFeatureOn(feature.key, edit ? edit_vault : vault)"
            :sync="true"
            :labels="false"
            :width="40"
            :height="22"
            :color="toggle_colors"
            :disabled="!edit"
            @change="toggleFeature(feature.key)"
          ></toggle-button>
          <label
            class="block"
            :class="{ 'cursor-pointer': edit }"
            @click="edit && toggleFeature(feature.key)"
          >
            <span class="font-medium block">
              {{ feature.label }}
              <span
                v-if="feature.experimental"
                class="ml-1 px-2 py-px rounded-full text-xs font-normal bg-grey-lighter text-grey-darker align-middle"
              >experimental</span>
            </span>
            <span class="text-xs text-grey-darker block mt-1">{{ feature.description }}</span>
          </label>
        </div>
        </div>
      </div>

      <!-- Integrations: MRI Strata Master sync now (PRD-4-5), only on vaults with a confirmed plan link.
           Shown in both modes so the page does not jump when editing starts. -->
      <!-- Keys on the conditional sections: v-is-authorized sets an inline display on its element,
           and Vue 2 would otherwise reuse that element for a neighbouring section. -->
      <div
        class="pb-2"
        key="integrations"
        v-if="vault_type == 'strata' && vault.mri_sync_available"
        v-is-authorized="{
          roles: [role],
          permissions: ['can_edit_vault_details'],
        }"
      >
        <p class="mt-6 mb-1 pb-1 border-b text-lg">
          <i class="fa fa-plug text-brand"></i> Integrations
        </p>
        <div
          class="flex flex-col md:flex-row md:items-center md:justify-between py-2 border-b border-grey-lighter"
        >
          <div class="md:pr-8">
            <span class="font-medium block">Strata Master</span>
            <span class="text-xs text-grey-darker block mt-1">
              This vault receives documents from MRI Strata Master every hour.
              Press Sync now to check for new documents straight away.
            </span>
          </div>
          <button
            type="button"
            class="
              border border-brand
              px-4
              py-2
              mt-3
              md:mt-0
              text-brand
              rounded
              font-medium
              flex-none
              hover:bg-brand hover:text-white
            "
            :class="{ 'opacity-50 cursor-not-allowed': mri_syncing || mri_sync_locked }"
            :disabled="mri_syncing || mri_sync_locked"
            @click.prevent="syncFromStrataMaster"
          >
            <i class="fa fa-sync-alt mr-2" :class="{ 'fa-spin': mri_syncing }"></i>
            {{ mri_sync_locked ? "Sync requested" : "Sync now" }}
          </button>
        </div>
      </div>

      <!-- Members who are not the owner can leave; the owner cannot (the API refuses too) -->
      <div
        class="mt-6 p-4 rounded bg-red-lightest border border-red-lighter flex flex-col md:flex-row md:items-center md:justify-between"
        v-if="vault.user_id != user.id"
      >
        <div class="md:pr-8">
          <span class="font-medium block text-red-dark">Leave this vault</span>
          <span class="text-xs text-grey-darker block mt-1">
            You will lose access to {{ vault.name }} and its documents. A vault manager would need to invite you again.
          </span>
        </div>
        <button
          type="button"
          class="flex-none mt-3 md:mt-0 border border-red bg-white text-red text-sm font-medium py-2 px-4 rounded hover:bg-red hover:text-white"
          @click.prevent="$refs.leave_vault_confirm.open()"
        >
          <i class="fa fa-sign-out-alt mr-1"></i> Leave vault
        </button>
      </div>

      <sweet-modal overlay-theme="dark" icon="error" ref="update_error">
        <p class="font-medium">An Error Occured</p>
        <p class="mt-4">{{ last_err }}</p>
      </sweet-modal>

      <sweet-modal
        icon="warning"
        overlay-theme="dark"
        ref="leave_vault_confirm"
      >
        <div class="flex flex-col">
          <p class="font-medium text-lg">Leave {{ vault.name }}?</p>
          <p class="mt-2 text-grey-darker">
            You will lose access to this vault and its documents. A vault manager would need to invite you again.
          </p>

          <div class="mt-6 flex justify-center">
            <button
              type="button"
              class="border border-grey bg-white px-6 py-2 text-grey-darker rounded font-medium mr-2 hover:bg-grey-lightest"
              @click.prevent="$refs.leave_vault_confirm.close()"
            >
              Cancel
            </button>
            <button
              type="button"
              class="border border-red bg-red px-6 py-2 text-white rounded font-medium hover:bg-red-dark"
              :class="{ 'opacity-50 cursor-not-allowed': leaving }"
              :disabled="leaving"
              @click.prevent="leaveVault()"
            >
              <i class="fa mr-1" :class="leaving ? 'fa-spin fa-spinner' : 'fa-sign-out-alt'"></i>
              {{ leaving ? "Leaving" : "Leave vault" }}
            </button>
          </div>
        </div>
      </sweet-modal>
    </form>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { syncNowOutcome } from "../mri_sync.mjs";

export default {
  data() {
    return {
      updating: false,
      edit: false,
      country: {},

      countries: [
        { code: "AU", name: "Australia" },
        { code: "NZ", name: "New Zealand" },
      ],

      edit_vault: {},
      last_err: "",
      // Features only render on strata, so the brand blue; grey from the Tailwind palette.
      toggle_colors: { checked: "#4296d4", unchecked: "#B8BECC" },

      mri_syncing: false,
      mri_sync_locked: false,
      mri_sync_timer: null,
      leaving: false,
    };
  },

  beforeDestroy() {
    clearTimeout(this.mri_sync_timer);
  },

  methods: {
    isNZStates(state) {
      return [
          "Body Corporate",
          "Residents Association",
          "Incorporated Society",
          "Cross Lease (XL)",
          "NZ"
      ].includes(state);
    },
    toggleAutoTagging() {
      if(this.edit_vault.auto_tagging == 'openai') {
        this.edit_vault.auto_tagging = 'off';
      } else {
        this.edit_vault.auto_tagging = 'openai';
      }

      if (this.edit_vault.auto_tagging == 'off') {
        this.edit_vault.auto_file = false;
      }
    },
    toggleAutoFile() {
      if (this.edit_vault.auto_tagging == 'off') {
        this.$noty.warning("Enable auto tagging to use this feature.");
        return;
      }
      this.edit_vault.auto_file = !this.edit_vault.auto_file;
    },
    toggleKeyDocument() {
      this.edit_vault.key_document = !this.edit_vault.key_document;
    },
    toggleEventAutomation() {
      this.edit_vault.event_automation = !this.edit_vault.event_automation;
    },
    toggleDuplicateCheck() {
      this.edit_vault.duplicate_check = !this.edit_vault.duplicate_check;
    },
    isFeatureOn(key, source) {
      if (key === "auto_tagging") {
        return source.auto_tagging === "openai";
      }
      return Boolean(source[key]);
    },
    toggleFeature(key) {
      const handlers = {
        auto_tagging: this.toggleAutoTagging,
        auto_file: this.toggleAutoFile,
        duplicate_check: this.toggleDuplicateCheck,
        key_document: this.toggleKeyDocument,
        event_automation: this.toggleEventAutomation,
      };
      handlers[key]();
    },
    convertToGB(mb) {
      return _.floor(mb / 1024, 2);
    },
    rounded(mb) {
      return _.floor(mb, 2);
    },
    validateBeforeSubmit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.updating = true;
          let t = this;

          let state = this.edit_vault.state
              ? this.edit_vault.state.code
              : this.edit_vault.state;

          axios
            .patch(`/api/vaults/${this.$route.params.vault_id}`, {
              name: this.edit_vault.name,
              strata_plan_number: this.edit_vault.strata_plan_number,
              address: this.edit_vault.address,
              suburb: this.edit_vault.suburb,
              state: state,
              postcode: this.edit_vault.postcode,
              number_of_lots: this.edit_vault.number_of_lots
                ? this.edit_vault.number_of_lots
                : 0,
              building_type: this.edit_vault.building_type,
              auto_tagging: this.edit_vault.auto_tagging == 'openai',
              auto_file: this.edit_vault.auto_file,
              event_automation: this.edit_vault.event_automation,
              duplicate_check: this.edit_vault.duplicate_check,
              key_document: this.edit_vault.key_document,
            })
            .then(({ data }) => {
              this.updating = false;
              this.edit = false;
              if (data.success) {
                this.$noty.success("Vault details has been updated", {
                  killer: true,
                  timeout: 1500,
                });
                this.$ebus.$emit("refresh-vaults");
              }
            })
            .catch(function (error) {
              t.updating = false;
              t.edit = false;
              if (error.response) {
                if (!error.response.success) {
                  t.last_err = error.response.data.message;
                }
              } else {
                t.last_err = error.message;
              }
              t.$refs.update_error.open();
            });
        }
      });
    },

    editDetails() {
      this.edit_vault = _.cloneDeep(this.vault);
      if (this.isNZStates(this.edit_vault.state)) {
        this.country = this.countries.find((country) => country.code == "NZ");
      } else {
        this.country = this.countries.find((country) => country.code == "AU");
      }
      // The API stores the state as its code; the multiselect wants the option object.
      const current = this.states.find((state) => state.code == this.edit_vault.state);
      this.edit_vault.state = current || this.edit_vault.state;
      this.edit = true;
    },

    syncFromStrataMaster() {
      if (this.mri_syncing || this.mri_sync_locked) {
        return;
      }
      this.mri_syncing = true;

      axios
        .post(`/api/vaults/${this.$route.params.vault_id}/mri/sync`)
        .then(({ status, data }) => this.finishStrataMasterSync(status, data))
        .catch((error) => {
          const response = error.response || {};
          this.finishStrataMasterSync(response.status, response.data);
        });
    },

    finishStrataMasterSync(status, data) {
      this.mri_syncing = false;
      const outcome = syncNowOutcome(status, data);

      if (outcome.kind === "success") {
        this.$noty.success(outcome.message, { killer: true, timeout: 4000 });
      } else if (outcome.kind === "cooldown") {
        this.$noty.warning(outcome.message, { killer: true, timeout: 4000 });
      } else {
        this.$noty.error(outcome.message, { killer: true, timeout: 4000 });
      }

      if (outcome.kind === "unavailable") {
        // The link was removed or the feature switched off: pick up the new flag.
        this.$ebus.$emit("refresh-vaults");
      }

      if (outcome.lockSeconds > 0) {
        this.mri_sync_locked = true;
        clearTimeout(this.mri_sync_timer);
        this.mri_sync_timer = setTimeout(() => {
          this.mri_sync_locked = false;
        }, outcome.lockSeconds * 1000);
      }
    },

    leaveVault() {
      if (this.leaving) {
        return;
      }
      this.leaving = true;
      axios
        .delete(`api/vaults/${this.$route.params.vault_id}/users/leave`)
        .then(({ data }) => {
          this.leaving = false;
          if (data.success) {
            this.$refs.leave_vault_confirm.close();
            this.$ebus.$emit("refresh-vaults");
            this.$router.push({
              name: "vaults",
            });
          }
        })
        .catch((error) => {
          this.leaving = false;
          this.$refs.leave_vault_confirm.close();
          const response = error.response || {};
          this.last_err = (response.data && response.data.message) || error.message;
          this.$refs.update_error.open();
        });
    },
  },

  computed: {
    ...mapGetters({
      vault: "getCurrentVault",
      role: "getUserRole",
      user: "getUser",
    }),
    vault_name() {
      return _.startCase(window.vault_type);
    },
    features() {
      return [
        {
          key: "auto_tagging",
          label: "Auto Tagging",
          description: "Uses machine learning to pre-select tags for uploaded files.",
          experimental: true,
        },
        {
          key: "auto_file",
          label: "Auto File",
          description: "Files uploaded documents automatically. Needs Auto Tagging.",
          experimental: true,
        },
        {
          key: "duplicate_check",
          label: "Duplicate Check",
          description: "Checks newly uploaded documents for duplicate files.",
          experimental: true,
        },
        {
          key: "key_document",
          label: "Key Document",
          description: "Shows the Key Document panel on the vault dashboard.",
          experimental: false,
        },
        {
          key: "event_automation",
          label: "Event Automation",
          description: "Work orders, agendas, minutes and invoices may create or attach to vault events.",
          experimental: false,
        },
      ];
    },
    vault_type() {
      return window.vault_type;
    },
    storageUsedPercent() {
      const max = Number(this.vault.max_storage_in_megabytes) || 0;
      if (max <= 0) {
        return 0;
      }
      const used = Number(this.vault.total_size_in_megabytes) || 0;
      return _.clamp(_.round((used / max) * 100, 1), 0, 100);
    },
    states() {
      if (this.country && this.country.code == "NZ") {
        return [
          { code: "Body Corporate", name: "Body Corporate" },
          { code: "Residents Association", name: "Residents Association" },
          { code: "Incorporated Society", name: "Incorporated Society" },
          { code: "Cross Lease (XL)", name: "Cross Lease (XL)" }
        ];
      }

      return [
          { code: "ACT", name: "Australian Capital Territory" },
          { code: "NSW", name: "New South Wales" },
          { code: "NT", name: "Northern Territory" },
          { code: "QLD", name: "Queensland" },
          { code: "SA", name: "South Australia" },
          { code: "TAS", name: "Tasmania" },
          { code: "VIC", name: "Victoria" },
          { code: "WA", name: "Western Australia" },
      ];
    },
    isNZ() {
      if (this.country && this.country.code == "NZ") {
        this.edit_vault.state = "";
        return true;
      }
      return false;
    },
  },
};
</script>

<style>
/* View-mode value: same height and top margin as the edit input (1px border, py-2, mt-1),
   so the page does not jump when switching modes */
.field-value {
  margin-top: 0.25rem;
  padding: 0.5rem 0;
  border: 1px solid transparent;
  border-bottom-color: #f1f3f8; /* grey-lighter */
  line-height: 1.5;
}

.multiselect__tags {
  min-height: 0px;
  display: block;
  padding: 6px 40px 0 8px;
  border-radius: 5px;
  border: 1px solid #dadee7;
  background: #fff;
}
/* On this page only: the same 42px box as the text inputs and the view-mode values */
.vault-details .multiselect {
  min-height: 42px;
}
.vault-details .multiselect__tags {
  min-height: 42px;
  height: 42px;
  padding: 9px 40px 0 12px;
}
.vault-details .multiselect__select {
  height: 40px;
}
/* .invalid > .multiselect__tags {
  border: 1px solid #e3342f;
} */
.multiselect__input {
  font-size: 16px;
}
</style>
